"use client";

import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { StrapiChart } from "@/types";

interface ChartsContextValue {
  charts: StrapiChart[];
}

const ChartsContext = createContext<ChartsContextValue>({ charts: [] });

export function ChartsProvider({
  charts: initialCharts,
  children,
}: {
  charts: StrapiChart[];
  children: ReactNode;
}): React.ReactElement {
  const [charts, setCharts] = useState(initialCharts);

  useEffect(() => {
    if (initialCharts.length > 0) setCharts(initialCharts);
  }, [initialCharts]);

  useEffect(() => {
    if (charts.length > 0) return;
    let cancelled = false;
    fetch("/api/charts")
      .then((res) => (res.ok ? res.json() : []))
      .then((data: unknown) => {
        if (!cancelled && Array.isArray(data) && data.length > 0) setCharts(data as StrapiChart[]);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [charts.length]);

  return (
    <ChartsContext.Provider value={{ charts }}>
      {children}
    </ChartsContext.Provider>
  );
}

export function useCharts(): ChartsContextValue {
  return useContext(ChartsContext);
}
